import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import classnames from 'classnames';

import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Button from '@adsk/bim360-matrix-react-button';
import SaveButton from '@adsk/bim360-matrix-react-save-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import InfoPopover from '@adsk/bim360-matrix-react-info-popover';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import { projectShape } from '@adsk/bim360-matrix-react-helpers/es/propTypes';
import Label from '@adsk/bim360-matrix-react-label';
import TextInput from '@adsk/bim360-matrix-react-text-input';
import Well from '@adsk/bim360-matrix-react-well';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import infoCircleSmall from '@adsk/bim360-matrix-react-assets/info-circle-small.svg';
import { aggregatedOptionsType, aggregatedSettingsType } from './helpers/propTypes';

import ModalSection from './ModalSection';
import EmailFrequencyTable from './EmailFrequencyTable';
import LinkButton from './LinkButton';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_NotificationsSettingsModal.scss';

const CHANGE_EMAIL_LINK = 'https://accounts.autodesk.com/Profile/Security';

export class NotificationsSettingsModal extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);

    this.renderBody = this.renderBody.bind(this);
    this.renderFooter = this.renderFooter.bind(this);
  }

  renderBody() {
    const {
      project,
      user,
      settings,
      options,
      canEdit,
      isLoading,
      isSaving,
      isError,
      onTryAgain,
      onChange,
      onReset
    } = this.props;
    const hasProject = project && project.name;
    const projectName = hasProject
      ? project.name
      : this.props[TSymbolProp].translate('NOT_SELECTED');
    const isDataEmpty = !isLoading && !isError && !Object.keys(settings).length;

    const wellTextTranslationKey = !hasProject
      ? 'NO_PROJECT_SELECTED_INFO'
      : !canEdit
        ? 'ADMIN_PREVENTION_INFO'
        : isDataEmpty
          ? 'EMPTY_STATE_TEXT'
          : '';
    const wellText = wellTextTranslationKey
      ? this.props[TSymbolProp].translate(wellTextTranslationKey)
      : '';
    const showContent = !isError && !isLoading;
    const showTable = hasProject && !isDataEmpty;
    const wellClassnames = classnames('NotificationsSettingsModal__well', {
      'NotificationsSettingsModal__well--margin-bottom': showTable
    });
    return (
      <div className="NotificationsSettingsModal__body">
        {isLoading && <Loader />}
        {isError && (
          <EmptyState
            className="NotificationsSettingsModal__error"
            text={this.props[TSymbolProp].translate('DATA_DIDNT_LOAD')}
            icon={EmptyState.Icons.CONE}
            footer={
              <Button styleType={Button.StyleType.INLINE_LINK} onClick={onTryAgain}>
                {this.props[TSymbolProp].translate('TRY_AGAIN')}
              </Button>
            }
          />
        )}
        {showContent && (
          <ModalSection title={this.props[TSymbolProp].translate('GENERAL')}>
            <div className="NotificationsSettingsModal__field-container">
              <div className="NotificationsSettingsModal__field">
                <Label>{this.props[TSymbolProp].translate('SEND_EMAIL')}</Label>
                <LinkButton
                  link={CHANGE_EMAIL_LINK}
                  label={this.props[TSymbolProp].translate('CHANGE')}
                  className="NotificationsSettingsModal__link-button"
                />
              </div>
              <TextInput value={user.email} disabled={true} />
            </div>
          </ModalSection>
        )}
        {showContent && (
          <ModalSection title={this.props[TSymbolProp].translate('PROJECT')} subtitle={projectName}>
            {wellText && (
              <Well className={wellClassnames}>
                <SvgIcon className="NotificationsSettingsModal__well-icon">
                  {infoCircleSmall}
                </SvgIcon>
                <div className="NotificationsSettingsModal__well-text">{wellText}</div>
              </Well>
            )}
            {showTable && (
              <div className="NotificationsSettingsModal__table-title">
                <Label>{this.props[TSymbolProp].translate('NOTIFICATIONS_FREQUENCY')}</Label>
                <InfoPopover
                  message={this.props[TSymbolProp].translate('EMAIL_SETTINGS_INFO')}
                  className="NotificationsSettingsModal__info-icon"
                  tooltipClassName="NotificationsSettingsModal__info-tooltip"
                  place={Tooltip.Places.TOP}
                />
              </div>
            )}
            {showTable && (
              <EmailFrequencyTable
                settings={settings}
                options={options}
                onChange={onChange}
                onReset={onReset}
                disabled={!canEdit || isSaving}
                canEdit={canEdit}
              />
            )}
          </ModalSection>
        )}
      </div>
    );
  }

  renderFooter() {
    const { isDirty, canEdit, isLoading, isError, isSaving, onSave, onExited } = this.props;
    const saveDisabled = !canEdit || isLoading || isError || !isDirty;
    return (
      <div className="NotificationsSettingsModal__footer">
        <div className="NotificationsSettingsModal__footer-buttons">
          <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={onExited}>
            {this.props[TSymbolProp].translate('CANCEL')}
          </Button>
          <SaveButton
            saveText={this.props[TSymbolProp].translate('SAVE')}
            savingText={this.props[TSymbolProp].translate('SAVING')}
            onSave={onSave}
            isSaving={isSaving}
            disabled={saveDisabled}
          />
        </div>
      </div>
    );
  }

  /**
   * render function
   */
  render() {
    return (
      <SimpleModal
        title={this.props[TSymbolProp].translate('NOTIFICATION_SETTINGS')}
        backdrop="static"
        footer={this.renderFooter()}
        show={this.props.show}
        onExited={this.props.onExited}
        className="NotificationsSettingsModal"
        disableModalExit={this.props.isSaving}
      >
        {this.renderBody()}
      </SimpleModal>
    );
  }
}

NotificationsSettingsModal.propTypes = {
  /**
   * The user's aggregated notification settings
   */
  settings: aggregatedSettingsType,
  /**
   * The user's aggregated notification options
   */
  options: aggregatedOptionsType,
  /**
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * Callback to when closing the modal.
   */
  onExited: PropTypes.func,
  /**
   * A callback that is called when data is changed in the settings
   */
  onChange: PropTypes.func,
  /**
   * A callback that is called when user resets data in settings
   */
  onReset: PropTypes.func,
  /**
   * Whether the user has changed any field
   */
  isDirty: PropTypes.bool,
  /**
   * A callback function for the modal save (when clicking on the save button)
   */
  onSave: PropTypes.func,
  /**
   * Whether or not is saving.
   */
  isSaving: PropTypes.bool,
  /**
   * The locale to use for the modal
   */
  locale: PropTypes.string,
  /**
   * The current project to show the notification settings for
   */
  project: projectShape,
  /**
   * The user to show the notification settings for
   */
  user: PropTypes.shape({
    /**
     * The email of the user
     */
    email: PropTypes.string.isRequired
  }),
  /**
   * whether the user has permissions to edit the settings
   */
  canEdit: PropTypes.bool,
  /**
   * Whether or not is loading.
   */
  isLoading: PropTypes.bool,
  /**
   * Whether or not failed to load data from BE.
   */
  isError: PropTypes.bool,
  /**
   * Try again error state handler
   */
  onTryAgain: PropTypes.func.isRequired
};

export default withLocale(
  NotificationsSettingsModal,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
